/**
 * Block-related mutations for the Command Engine.
 *
 * Houses the authoring entry points for BLOCK / INSERT / EXPLODE that
 * go beyond the simpler `createBlockInsertEntity` helper in
 * `geometryMutations.ts`. The geometry mutations file re-exports the
 * functions below via a barrel so callers keep their existing import
 * path (`@dxf-ai-editor/command-engine`).
 *
 * Public surface (all re-exported by geometryMutations.ts):
 *   - validateBlockName(name, existingBlocks) -> null | reason
 *   - detectCyclicBlockReference(blockName, childInsertName, blocks) -> boolean
 *   - createBlockDefinitionMutation(input) -> BlockDefinitionMutationResult
 *   - buildBlockInsertEntityFull(input) -> GeometryMutationResult  (extended)
 *   - explodeInsertMutation(input) -> ExplodeInsertResult
 *
 * The explode math uses a pure 2D affine matrix (BlockMatrix below) in
 * MODEL space — CCW-positive rotation, the DXF/AutoCAD convention (see
 * `composeInsertMatrix`'s doc; @codex round-20). It shares only the
 * {a,b,c,d,e,f} column LAYOUT with apps/web/src/app/scene/blockMatrix.ts;
 * that render-side copy is authored in the SVG sign convention (negated
 * rotation under a parent `scale(1,-1)` group), which is a rendering
 * concern and must NOT leak into model-space math. The two copies stay
 * intentionally independent (command-engine has no web-app dependency).
 */

import type { EntityPreview } from "@dxf-ai-editor/contracts";
import type {
  BlockChildEntity,
  BlockDefinition,
  BlockInsertAttribute,
  BlockInsertEntity as DrawingBlockInsertEntity,
  BlockRecord,
  DrawingPoint,
} from "@dxf-ai-editor/drawing-model";
import {
  coerceColor,
  computeBlockUnitFactor,
  isAnonymousBlockName,
  serializeColor,
  type EntityColor,
} from "@dxf-ai-editor/drawing-model";

import {
  buildLwpolylinePreviewEntity,
  buildTextPreviewEntity,
  buildMtextPreviewEntity,
  buildBlockInsertPreviewEntity,
  buildEllipsePreviewEntity,
} from "./previewBuilders";
import {
  buildSimpleGeometryPreviewEntity,
  type SimpleArcEntity,
  type SimpleBlockInsertEntity,
  type SimpleCircleEntity,
  type SimpleEllipseEntity,
  type SimpleLineEntity,
  type SimpleLwpolylineEntity,
  type SimpleMtextEntity,
  type SimpleTextEntity,
} from "./simpleGeometry";
// SL-15 — shared ONE-TRUTH 3D ellipse-explode math (also used by the apps/web
// reducer explode arm). Keeps the transient shell preview byte-identical to the
// reconciled document (shell↔doc parity).
import { computeExplodedEllipseGeometry } from "./ellipseExplodeGeometry";
// #470 (Lane F) — shared ONE-TRUTH world-vs-block-local ATTRIB placement resolver
// (also used by the apps/web reducer explode path) so a set `overridePosition`
// world point is never double-transformed on EXPLODE (shell↔doc parity).
import {
  guardScale,
  resolveExplodedAttribPlacement,
  resolveMinsertCellOffset,
} from "./attribExplodePlacement";
// EXPLODE-COMPOUND (§5.11) — shared ONE-TRUTH nested-INSERT placement math (also
// used by the apps/web reducer explode arm) so a single-level EXPLODE re-emits a
// nested INSERT with a composed 3-axis scale (shell↔doc parity).
import { composeNestedInsertPlacement } from "./nestedInsertExplode";
// EXPLODE x heavy POLYLINE / ACAD_TABLE / OLE2FRAME — the ONE narrowing both
// EXPLODE arms use, so their `case` labels and the carry module's contract
// cannot drift (see `explodeInsertMutation` for why THIS path declines all
// three kinds while the persisted reducer carries them).
import { isPolylineTableOleChild } from "./explodeBlockChildPolylineTableOle";

// ═══════════════════════════════════════════════════════════════
// Block name validation & cycle detection
// ═══════════════════════════════════════════════════════════════

// DXF does not allow these characters in symbol names. The list
// mirrors the constraint enforced by AutoCAD's BMAKE / RENAME dialog.
//
// Reserved-prefix detection (`*Model_Space`, `*Paper_Space*`, `*D\d+`,
// `*U\d+`, `*X\d+`, `*E\d+`) lives in `@dxf-ai-editor/drawing-model`
// `blockNaming.ts` — we reuse `isAnonymousBlockName` here so the two
// enforcers can never drift.
const FORBIDDEN_BLOCK_NAME_CHARS = /[|<>/\\:;,"?*=]/;

export type BlockNameValidationError =
  | "empty"
  | "too-long"
  | "reserved"
  | "forbidden-chars"
  | "duplicate";

/**
 * Validate a user-supplied block name against AutoCAD's authoring rules.
 * Returns null when the name is acceptable; otherwise returns a
 * structured reason the caller can localise. The `existingBlocks` map
 * is consulted for duplicate detection; case-insensitive match is
 * used to mirror AutoCAD's behaviour.
 */
export function validateBlockName(
  name: string,
  existingBlocks: Record<string, BlockDefinition> = {},
): BlockNameValidationError | null {
  const trimmed = name.trim();
  if (trimmed.length === 0) return "empty";
  if (trimmed.length > 255) return "too-long";
  if (isAnonymousBlockName(trimmed)) return "reserved";
  // Include ASCII control characters (0x00-0x1F and 0x7F) in the
  // forbidden set. The regex literal cannot contain raw control
  // bytes portably, so we test via char-code.
  for (let i = 0; i < trimmed.length; i++) {
    const code = trimmed.charCodeAt(i);
    if (code < 0x20 || code === 0x7f) return "forbidden-chars";
  }
  if (FORBIDDEN_BLOCK_NAME_CHARS.test(trimmed)) return "forbidden-chars";
  // Duplicate check (case-insensitive). Skip reserved names in the
  // existing map — they are always present but do not collide with
  // user blocks because the user form was rejected above.
  const lower = trimmed.toLowerCase();
  for (const existing of Object.keys(existingBlocks)) {
    if (existing.toLowerCase() === lower) return "duplicate";
  }
  return null;
}

/**
 * Detect whether adding an INSERT of `candidateChildInsertName` inside
 * the block `blockName` would introduce a cycle in the block
 * definition graph (A → B → A). DFS walks every INSERT child recursively
 * and returns `true` as soon as `blockName` is reached.
 *
 * Caller invariant: `blockName` is the block being authored / extended.
 * The candidate's definition must already exist in `blocks` when this
 * is called; otherwise the call returns false (no edges to traverse).
 */
export function detectCyclicBlockReference(
  blockName: string,
  candidateChildInsertName: string,
  blocks: Record<string, BlockDefinition>,
): boolean {
  if (blockName === candidateChildInsertName) return true;
  const candidate = blocks[candidateChildInsertName];
  if (!candidate) return false;
  const visited = new Set<string>([candidateChildInsertName]);
  const stack: string[] = [];
  for (const entity of candidate.entities) {
    if (entity.type === "Block") {
      stack.push(entity.blockName);
    }
  }
  while (stack.length > 0) {
    const current = stack.pop();
    if (!current) continue;
    if (current === blockName) return true;
    if (visited.has(current)) continue;
    visited.add(current);
    const def = blocks[current];
    if (!def) continue;
    for (const child of def.entities) {
      if (child.type === "Block") {
        stack.push(child.blockName);
      }
    }
  }
  return false;
}

// ═══════════════════════════════════════════════════════════════
// createBlockDefinitionMutation
// ═══════════════════════════════════════════════════════════════

export type BlockPostAction = "retain" | "convert" | "delete";

export interface CreateBlockDefinitionInput {
  blockDef: BlockDefinition;
  blockRecord: BlockRecord;
  postAction: BlockPostAction;
  /** Required when postAction is "convert" or "delete". */
  sourceEntityIds?: string[];
  /** Required when postAction is "convert". */
  convertInsertPoint?: DrawingPoint;
  /** Target space for the newly-created INSERT (convert). */
  convertSpaceId?: string;
  /** Id to use for the new INSERT in "convert" mode. */
  convertInsertId?: string;
  /** Existing blocks map, used for duplicate-name detection. */
  existingBlocks?: Record<string, BlockDefinition>;
}

export interface CreateBlockDefinitionResult {
  /** Added or updated block definition (keyed by name elsewhere). */
  blockDef: BlockDefinition;
  /** Matching BlockRecord entry (TABLES-section metadata). */
  blockRecord: BlockRecord;
  /** Entity IDs to delete from their owning space. */
  removedEntityIds: string[];
  /** New INSERT preview entity, when postAction === "convert". */
  insertPreview?: EntityPreview;
  summary: string;
}

export function createBlockDefinitionMutation(
  input: CreateBlockDefinitionInput,
): CreateBlockDefinitionResult {
  const { blockDef, blockRecord, postAction } = input;

  const nameError = validateBlockName(blockDef.name, input.existingBlocks);
  if (nameError !== null) {
    throw new Error(`Invalid block name "${blockDef.name}": ${nameError}`);
  }

  const removedEntityIds: string[] = [];
  let insertPreview: EntityPreview | undefined;
  let summary = `BLOCK ${blockDef.name} created (${postAction})`;

  if (postAction === "delete") {
    if (!input.sourceEntityIds || input.sourceEntityIds.length === 0) {
      throw new Error('postAction "delete" requires sourceEntityIds');
    }
    removedEntityIds.push(...input.sourceEntityIds);
    summary += `; ${input.sourceEntityIds.length} source entit${input.sourceEntityIds.length === 1 ? "y" : "ies"} deleted`;
  } else if (postAction === "convert") {
    if (!input.sourceEntityIds || input.sourceEntityIds.length === 0) {
      throw new Error('postAction "convert" requires sourceEntityIds');
    }
    if (!input.convertInsertPoint) {
      throw new Error('postAction "convert" requires convertInsertPoint');
    }
    if (!input.convertSpaceId) {
      throw new Error('postAction "convert" requires convertSpaceId');
    }
    if (!input.convertInsertId) {
      throw new Error('postAction "convert" requires convertInsertId');
    }
    removedEntityIds.push(...input.sourceEntityIds);

    const insertGeom: SimpleBlockInsertEntity = {
      id: input.convertInsertId,
      type: "Block",
      spaceId: input.convertSpaceId,
      blockName: blockDef.name,
      insertionPoint: {
        x: input.convertInsertPoint.x,
        y: input.convertInsertPoint.y,
      },
      scaleX: 1,
      scaleY: 1,
      scaleZ: 1,
      rotation: 0,
      columnCount: 1,
      rowCount: 1,
      columnSpacing: 0,
      rowSpacing: 0,
    };
    insertPreview = buildBlockInsertPreviewEntity(insertGeom, {
      summary: `Converted to INSERT of ${blockDef.name}`,
    });
    summary += `; converted to INSERT at ${input.convertInsertPoint.x},${input.convertInsertPoint.y}`;
  }

  return {
    blockDef,
    blockRecord,
    removedEntityIds,
    insertPreview,
    summary,
  };
}

// ═══════════════════════════════════════════════════════════════
// createBlockInsertEntityFull (extended version of createBlockInsertEntity)
// ═══════════════════════════════════════════════════════════════

export interface CreateBlockInsertFullInput {
  nextId: string;
  spaceId: string;
  blockName: string;
  insertionPoint: { x: number; y: number };
  defaults?: {
    scaleX?: number;
    scaleY?: number;
    scaleZ?: number;
    rotation?: number;
    columnCount?: number;
    rowCount?: number;
    columnSpacing?: number;
    rowSpacing?: number;
  };
  /**
   * Pre-bound user-supplied attribute values, keyed by ATTDEF tag.
   * When a tag has no user value, the ATTDEF's defaultValue is used.
   */
  userAttributeValues?: Record<string, string>;
  /** The block definition must exist. Used for validation + ATTDEF scan. */
  blockDefinition: BlockDefinition;
  /** Existing blocks map for cycle detection (unused when the INSERT
   *  is not itself inside a block definition). */
  existingBlocks?: Record<string, BlockDefinition>;
  /**
   * BlockRecord for the block being inserted. When present its
   * `insertionUnits` plus `drawingInsunits` drive AutoCAD's auto unit-
   * factor scaling (Task 2.10). Leave undefined to skip the conversion.
   */
  blockRecord?: BlockRecord;
  /**
   * Drawing-level INSUNITS (`$INSUNITS`). 0 = Unitless (no conversion).
   * Combined with `blockRecord.insertionUnits` through
   * `computeBlockUnitFactor` to auto-scale the INSERT.
   */
  drawingInsunits?: number;
  /**
   * When true (default), `computeBlockUnitFactor` is applied to the
   * final scaleX / scaleY / scaleZ. Callers that have already baked
   * the factor in (or explicitly want the raw user scales) can pass
   * false.
   */
  applyUnitFactor?: boolean;
}

export interface CreateBlockInsertFullResult {
  /** Preview entity (for display / property palette). */
  entity: EntityPreview;
  /** Complete BlockInsertEntity with materialised attributes. */
  insertEntity: DrawingBlockInsertEntity;
  summary: string;
}

/**
 * Extended INSERT authoring flow. Given the target block definition it
 * materialises ATTRIB children for every ATTDEF that is neither
 * constant nor preset (these two flags mean the value is baked into
 * the block and no ATTRIB is emitted). When the caller supplies a
 * value in `userAttributeValues`, that wins; otherwise the ATTDEF's
 * `defaultValue` is copied into the ATTRIB.
 */
export function createBlockInsertEntityFull(
  input: CreateBlockInsertFullInput,
): CreateBlockInsertFullResult {
  const { blockDefinition } = input;
  if (!blockDefinition) {
    throw new Error(`Block ${input.blockName} not found`);
  }
  if (blockDefinition.name !== input.blockName) {
    throw new Error(
      `Mismatched block: expected ${input.blockName}, got ${blockDefinition.name}`,
    );
  }

  const attributes: BlockInsertAttribute[] = [];
  for (const child of blockDefinition.entities) {
    if (child.type !== "Attdef") continue;
    // Constant ATTDEFs are baked into the block, no ATTRIB emitted.
    if (child.flags.constant) continue;
    // Preset ATTDEFs skip the prompt but still emit an ATTRIB with
    // their default value. AutoCAD behaves this way so the attribute
    // is visible (and editable via EATTEDIT) after INSERT.
    const userValue = input.userAttributeValues?.[child.tag];
    // Round-5 FINDING 1 — a freshly authored ATTRIB deliberately leaves
    // `overridePosition` UNSET (matching the value-only-create precedent,
    // `attributeMutations.ts updateAttributeValueMutation` round-3 FINDING 1+4).
    // `overridePosition` is the SYSTEM-WIDE "explicit WORLD placement"
    // discriminator: the import parser + EATTEDIT write a world point there and
    // the export encoder (`dxfBridge.attribExportPosition.ts`) + the scene3d
    // render (`attdefFromEntity.ts resolvePlacement`) BOTH treat a set value as
    // absolute. Seeding it here with the ATTDEF's BLOCK-LOCAL `insertionPoint`
    // therefore mis-typed a local point as world — the render placed the text
    // un-transformed (near the block origin instead of the INSERT) and the
    // export baked the local point into group 10/20. Leaving it unset makes the
    // attribute follow the ATTDEF template LIVE through the INSERT transform in
    // render AND resolve to the transformed template anchor on export — correct
    // and consistent. `overrideHeight/Rotation/Style` stay seeded (behaviourally
    // inert vs the `?? attdef.*` fallback, same as the value-only-create path).
    attributes.push({
      tag: child.tag,
      value: userValue ?? child.defaultValue,
      handle: "",
      overrideHeight: child.height,
      overrideRotation: child.rotation,
      overrideStyle: child.styleName,
      invisible: child.flags.invisible,
    });
  }

  const rawScaleX = input.defaults?.scaleX ?? 1;
  const rawScaleY = input.defaults?.scaleY ?? 1;
  const rawScaleZ = input.defaults?.scaleZ ?? 1;
  // Block Unit Factor (Task 2.10). AutoCAD auto-scales on INSERT when
  // the block's insertionUnits differ from the drawing's $INSUNITS so
  // the geometry keeps its physical size. Factor = 1 when either side
  // is Unitless (0) or when `applyUnitFactor` is disabled.
  const applyFactor = input.applyUnitFactor !== false;
  const blockInsunits = input.blockRecord?.insertionUnits ?? 0;
  const drawingInsunits = input.drawingInsunits ?? 0;
  const unitFactor = applyFactor
    ? computeBlockUnitFactor(drawingInsunits, blockInsunits)
    : 1;
  const scaleX = rawScaleX * unitFactor;
  const scaleY = rawScaleY * unitFactor;
  const scaleZ = rawScaleZ * unitFactor;
  const rotation = input.defaults?.rotation ?? 0;
  const columnCount = input.defaults?.columnCount ?? 1;
  const rowCount = input.defaults?.rowCount ?? 1;
  const columnSpacing = input.defaults?.columnSpacing ?? 0;
  const rowSpacing = input.defaults?.rowSpacing ?? 0;

  const insertEntity: DrawingBlockInsertEntity = {
    id: input.nextId,
    spaceId: input.spaceId,
    layer: "0",
    // EPIC-COLOR-UNION S1 — typed colour union default.
    color: { kind: "byLayer" },
    linetype: "ByLayer",
    lineweight: "ByLayer",
    transparency: "0",
    type: "Block",
    blockName: input.blockName,
    insertionPoint: input.insertionPoint,
    scaleX,
    scaleY,
    scaleZ,
    rotation,
    columnCount,
    rowCount,
    columnSpacing,
    rowSpacing,
    extrusion: { x: 0, y: 0, z: 1 },
    attributes,
    customProperties: {},
    annotationScales: [],
    annotativeContexts: [],
  };

  const previewGeom: SimpleBlockInsertEntity = {
    id: input.nextId,
    type: "Block",
    spaceId: input.spaceId,
    blockName: input.blockName,
    insertionPoint: input.insertionPoint,
    scaleX,
    scaleY,
    scaleZ,
    rotation,
    columnCount,
    rowCount,
    columnSpacing,
    rowSpacing,
  };
  const entity = buildBlockInsertPreviewEntity(previewGeom, {
    summary: `INSERT ${input.blockName} at ${input.insertionPoint.x},${input.insertionPoint.y}`,
    // Feed the BlockRecord + $INSUNITS pair into the preview so the
    // Properties-palette "Block Unit" / "Block Unit Factor" rows show
    // the conversion the auto-scale above actually applied, instead of
    // the Unitless defaults (factor 1). (#248 re-audit)
    //
    // Pass the APPLIED factor too: when `applyUnitFactor: false` forces
    // the scales to stay raw, the palette must show factor 1 — not a
    // recomputed unit-pair conversion that was never applied. Invariant:
    // displayed "Block Unit Factor" === the multiplier the insert's
    // scales actually received, in BOTH modes. (#301 r2)
    context: {
      blockRecord: input.blockRecord,
      drawingInsunits: input.drawingInsunits,
      appliedUnitFactor: unitFactor,
    },
  });

  return {
    entity,
    insertEntity,
    summary: `BLOCK INSERT created name=${input.blockName} at ${input.insertionPoint.x},${input.insertionPoint.y}; attributes=${attributes.length}`,
  };
}

// ═══════════════════════════════════════════════════════════════
// explodeInsertMutation
// ═══════════════════════════════════════════════════════════════

/**
 * 2D affine matrix in {a,b,c,d,e,f} column layout (the same LAYOUT as
 * apps/web/src/app/scene/blockMatrix.ts, but composed in MODEL space —
 * CCW-positive rotation, not the render copy's SVG sign). Pure math, no
 * JSX: this copy lives inside the command-engine so explode can run
 * without a rendering layer.
 *
 *     [ a  c  e ]
 *     [ b  d  f ]
 *     [ 0  0  1 ]
 */
type BlockMat = {
  a: number;
  b: number;
  c: number;
  d: number;
  e: number;
  f: number;
};

function mat(
  a: number,
  b: number,
  c: number,
  d: number,
  e: number,
  f: number,
): BlockMat {
  return { a, b, c, d, e, f };
}

function multiply(left: BlockMat, right: BlockMat): BlockMat {
  return mat(
    left.a * right.a + left.c * right.b,
    left.b * right.a + left.d * right.b,
    left.a * right.c + left.c * right.d,
    left.b * right.c + left.d * right.d,
    left.a * right.e + left.c * right.f + left.e,
    left.b * right.e + left.d * right.f + left.f,
  );
}

function transformPoint(m: BlockMat, p: { x: number; y: number }) {
  return {
    x: m.a * p.x + m.c * p.y + m.e,
    y: m.b * p.x + m.d * p.y + m.f,
  };
}

function transformVector(m: BlockMat, v: { x: number; y: number }) {
  // A vector (no translation) — drop the e/f terms.
  return {
    x: m.a * v.x + m.c * v.y,
    y: m.b * v.x + m.d * v.y,
  };
}

/**
 * Compose an INSERT's transform matrix in MODEL space:
 *     T(insertion) × R(+rotDeg) × S(sx, sy) × T(-basePoint)
 *
 * `+rotDeg` — CCW-positive, the DXF/AutoCAD convention (group 50; ezdxf
 * `Insert.matrix44()` composes `translate · z_rotate(+rotation) · scale ·
 * translate(-base)`), the SAME sign as `rotatePoint` (ROTATE command), the
 * typed render's `transformBlockDirection`, and `resolveMinsertCellOffset`.
 *
 * @codex round-20 "Keep MINSERT cell offsets in the matrix convention" — this
 * used to copy `blockMatrix.ts`'s `-rotDeg`, but THAT negation is an
 * SVG-AUTHORING artifact: the scene renderer's matrix is composed inside a
 * parent `scale(1,-1)` group (see `composeBlockMatrix`'s doc), so its sign is
 * flipped ON ENTRY to read correctly on a y-down screen. Model space has no
 * such wrapper — carrying the flipped sign here rotated exploded child
 * geometry CW for a CCW INSERT: opposite to the pre-explode render, opposite
 * to the (+θ) MINSERT per-cell grid step, and opposite to every composed
 * scalar rotation field (`child.rotation + parentRot`, arc
 * `startAngle/endAngle + parentRot`) in this very file.
 */
function composeInsertMatrix(input: {
  translate: { x: number; y: number };
  rotateDeg: number;
  scaleX: number;
  scaleY: number;
  basePoint?: { x: number; y: number };
}): BlockMat {
  const { translate: t, rotateDeg, scaleX, scaleY, basePoint } = input;
  const rad = (rotateDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const tMat = mat(1, 0, 0, 1, t.x, t.y);
  const rMat = mat(cos, sin, -sin, cos, 0, 0);
  const sMat = mat(scaleX, 0, 0, scaleY, 0, 0);
  const bx = basePoint?.x ?? 0;
  const by = basePoint?.y ?? 0;
  const bMat = mat(1, 0, 0, 1, -bx, -by);
  return multiply(tMat, multiply(rMat, multiply(sMat, bMat)));
}

export type ResolveNestedMode = "none" | "one-level";

export interface ExplodeInsertInput {
  /** The INSERT entity being exploded. */
  insert: DrawingBlockInsertEntity;
  /** Block definition referenced by the INSERT (must exist). */
  blockDefinition: BlockDefinition;
  /** Corresponding BlockRecord. Used to check explodable. */
  blockRecord?: BlockRecord;
  /** How nested INSERTs inside the block should be handled. */
  resolveNested: ResolveNestedMode;
  /** Allocator for freshly-generated child entity IDs. */
  allocateId: () => string;
  /** All blocks map (for resolveNested="one-level" lookup). */
  blocks: Record<string, BlockDefinition>;
}

export interface ExplodeInsertResult {
  /** Entities to remove (just the original INSERT) — EMPTY when the explode was declined. */
  removedEntityIds: string[];
  /** New world-space entities produced by the explode. */
  newEntities: EntityPreview[];
  summary: string;
  /**
   * The block-child kinds that made this preview DECLINE the whole explode,
   * empty when it ran. A refusal is total: `removedEntityIds` and
   * `newEntities` are both empty, so the shell can never show an INSERT
   * dissolved into a child set that silently omits part of its content.
   *
   * Today that is every heavy `POLYLINE`, `ACAD_TABLE` and `OLE2FRAME` child,
   * because this package has no `EntityPreview` projection for them — see
   * {@link explodeInsertMutation}'s own doc for why the decline here is
   * deliberately broader than the persisted reducer's, and for the completion
   * plan.
   */
  declinedChildTypes: readonly BlockChildEntity["type"][];
}

/**
 * Extract the layer / color / linetype / lineweight of a child when
 * inheriting ByBlock/ByLayer. Same rules as the renderer's
 * resolveByBlockLayer helper — duplicated here so explode can run
 * without the web-app dependency.
 */
function resolveChildLayer(childLayer: string, parentLayer: string): string {
  // Layer "0" inside a block means: inherit from parent INSERT. Any
  // other layer value is kept verbatim.
  return childLayer === "0" ? parentLayer : childLayer;
}

function resolveChildProp(childValue: string, parentValue: string): string {
  // ByBlock → inherit from the parent INSERT. ByLayer stays ByLayer
  // (the actual layer lookup is done by the renderer / style
  // resolver, not here).
  if (childValue === "ByBlock") return parentValue;
  return childValue;
}

/**
 * EPIC-COLOR-UNION S1 — colour counterpart of `resolveChildProp` operating on
 * the typed `EntityColor` union (the legacy variant compared the bare strings).
 * A `byBlock` child inherits the parent INSERT's colour; every other case keeps
 * the child's own colour.
 *
 * round-2 P2-C — the resolved colour is COMMITTED back through the property
 * translator (`commonFieldPatchValue → deserializeColor`) by any consumer that
 * persists this preview, so it must be LOSSLESS. `serializeColor` returns the
 * EXACT legacy string for the common cases (byLayer / byBlock / index / plain
 * rgb — byte-identical to the prior `colorToLegacyString` path, Category-B
 * preserved) and a `!EC:`-prefixed payload only for the rare rich colours
 * (rgb+aci/name, bookColor — including a `byBlock` child inheriting such an
 * INSERT), which otherwise collapsed to "#rrggbb"/"ByLayer" and DROPPED the
 * 62/430 siblings on explode. The committed document entity gets its OWN fresh
 * (lossy) display preview via `dxfGeometry`, so the display path is unaffected.
 */
function resolveChildColorSerialized(
  childColor: EntityColor,
  parentColor: EntityColor,
): string {
  // EPIC-COLOR-UNION S1 — coerce both operands before the `.kind` test.
  // Block children come through the bridge (always typed), but a raw-string
  // `color` slipping through (an entity literal that bypassed the schema —
  // `assembleDrawingDocument` does NOT re-parse in PROD) would have an undefined
  // `.kind`, be treated as not-byBlock, and feed `serializeColor` a string
  // → wrong value in the exploded child's `Color` bag. Coercing defends that.
  const child = coerceColor(childColor);
  const parent = coerceColor(parentColor);
  return serializeColor(child.kind === "byBlock" ? parent : child);
}

function cloneChildBase(
  child: BlockChildEntity,
  newId: string,
  parentInsert: DrawingBlockInsertEntity,
) {
  return {
    id: newId,
    spaceId: parentInsert.spaceId,
    layer: resolveChildLayer(child.layer, parentInsert.layer),
    // EPIC-COLOR-UNION S1 — `child.color` / `parentInsert.color` are the typed
    // union; resolve ByBlock inheritance then SERIALIZE losslessly for the
    // committed `Color` property bag below (byte-identical for ByLayer/ByBlock/
    // index/plain-rgb; `!EC:` payload preserves rich-colour 62/430 siblings).
    color: resolveChildColorSerialized(child.color, parentInsert.color),
    linetype: resolveChildProp(child.linetype, parentInsert.linetype),
    lineweight: resolveChildProp(child.lineweight, parentInsert.lineweight),
    transparency: child.transparency,
  };
}

function cellInsertPoint(
  parent: DrawingBlockInsertEntity,
  row: number,
  col: number,
): { x: number; y: number } {
  // @codex round-18 P2 (r3570000118) — the per-cell grid step rides the
  // block's own rotate+scale (render truth `transformBlockDirection`,
  // shared via `resolveMinsertCellOffset`), the SAME residual the WORLD
  // ATTRIB regime already uses (#482). Before this fix this origin fed the
  // child-geometry/block-local-ATTRIB `matrix` with the RAW unrotated
  // `(col·columnSpacing, row·rowSpacing)` step, so a rotated/scaled MINSERT
  // exploded the geometry at the wrong cell while the WORLD ATTRIB label
  // (which already used the rotated offset) landed correctly — splitting
  // the label from its geometry. ONE-TRUTH with the WORLD ATTRIB cellDelta
  // below: same helper, same inputs, so a cell's origin and its label
  // residual can never diverge again.
  const offset = resolveMinsertCellOffset({
    row,
    col,
    columnSpacing: parent.columnSpacing,
    rowSpacing: parent.rowSpacing,
    rotationDeg: parent.rotation,
    scaleX: parent.scaleX,
    scaleY: parent.scaleY,
  });
  return {
    x: parent.insertionPoint.x + offset.x,
    y: parent.insertionPoint.y + offset.y,
  };
}

export function explodeInsertMutation(
  input: ExplodeInsertInput,
): ExplodeInsertResult {
  const { insert, blockDefinition, blockRecord, resolveNested } = input;

  if (blockRecord && blockRecord.explodable === false) {
    throw new Error(`Block "${insert.blockName}" is not explodable`);
  }

  // ── EXPLODE is ALL-OR-NOTHING on this path too ──────────────────
  // This mutation used to drop a heavy POLYLINE / ACAD_TABLE / OLE2FRAME child
  // through `explodeChildEntity`'s `default:` arm while still reporting the
  // parent INSERT in `removedEntityIds` — the shell-preview twin of the silent
  // data loss the persisted reducer carried (@codex PR #785 round 13,
  // FINDING 1). It now declines the WHOLE explode instead: nothing removed,
  // nothing produced, so the preview can never show an INSERT dissolved into a
  // child set that quietly omits part of its content.
  //
  // The decline here is BROADER than the persisted reducer's, and deliberately
  // so — the two paths answer two different questions:
  //
  //   - the persisted reducer declines only a placement the child's own fields
  //     cannot absorb (`summarizeExplodeInsertRefusal`) and CARRIES the rest;
  //   - this preview declines EVERY one of the three kinds, because the
  //     question it has to answer is "can I project this child into an
  //     `EntityPreview`?", and in this package the answer is no for all three.
  //
  // KNOWN INCOMPLETE, documented per CLAUDE.md ПРИОРИТЕТ #1 rather than
  // silently half-done: there is no `buildPolylinePreviewEntity` /
  // `buildTablePreviewEntity` / `buildOleFramePreviewEntity` in
  // `./previewBuilders.ts`. The complete projections DO exist, in
  // `apps/web/src/app/state/dxfGeometry.ts` (`buildPolylinePreview` /
  // `buildTablePreview` / `buildOleFramePreview`), and the shell re-syncs from
  // THOSE after the document reducer commits — so a user still sees the
  // exploded children; only this optimistic pre-commit preview stands aside.
  // COMPLETING it means lifting those three builders into `./previewBuilders.ts`
  // as the one truth both sides project through (they currently depend on the
  // app's `t()` i18n and `resolveTableStyleName`, so it is a `dxfGeometry`
  // extraction of its own) — NOT a second, thinner property bag written here,
  // which would be exactly the "simplified property editing" ПРИОРИТЕТ #1
  // forbids.
  //
  // `declinedChildTypes` reports WHICH kinds stood the explode down, in the
  // order the definition lists them, so a caller can say so rather than
  // guessing from an empty result.
  const declinedChildTypes: BlockChildEntity["type"][] = [];
  for (const child of blockDefinition.entities) {
    if (!isPolylineTableOleChild(child)) continue;
    if (!declinedChildTypes.includes(child.type)) {
      declinedChildTypes.push(child.type);
    }
  }
  if (declinedChildTypes.length > 0) {
    return {
      removedEntityIds: [],
      newEntities: [],
      declinedChildTypes,
      summary: `EXPLODE ${insert.blockName}: declined — this preview cannot carry ${declinedChildTypes.join(", ")}`,
    };
  }

  const newEntities: EntityPreview[] = [];
  const parentRot = insert.rotation;
  const baseX = blockDefinition.basePoint.x;
  const baseY = blockDefinition.basePoint.y;
  // Render truth (`blockFromEntity.ts` builds `transform.scale` via
  // `guardScale` BEFORE anything consumes it — child geometry, grid step,
  // text heights alike): normalise degenerate scale components ONCE at the
  // entry, so the child-geometry affine, the block-local ATTRIB height and
  // the per-cell grid step all ride the SAME guarded scales the render drew
  // with. Without this a schema-valid `scaleX: 0` INSERT exploded its child
  // geometry flattened to zero x-extent while render (and the already-guarded
  // `resolveMinsertCellOffset` grid step) used scale 1 — the same divergence
  // class as @codex round-19 "Use guarded scales for MINSERT offsets".
  const scaleX = guardScale(insert.scaleX);
  const scaleY = guardScale(insert.scaleY);
  const scaleZ = guardScale(insert.scaleZ);

  const rowCount = Math.max(1, insert.rowCount);
  const colCount = Math.max(1, insert.columnCount);
  for (let row = 0; row < rowCount; row++) {
    for (let col = 0; col < colCount; col++) {
      const origin = cellInsertPoint(insert, row, col);
      const matrix = composeInsertMatrix({
        translate: origin,
        rotateDeg: parentRot,
        scaleX,
        scaleY,
        basePoint: { x: baseX, y: baseY },
      });

      // ── ATTRIB materialisation ────────────────────────────
      // Convert visible ATTRIBs attached to the INSERT into Text
      // entities at the transformed ATTDEF position. Invisible
      // ATTRIBs are skipped per AutoCAD's EXPLODE behaviour.
      for (const attrib of insert.attributes) {
        if (attrib.invisible) continue;
        const found = blockDefinition.entities.find(
          (e) => e.type === "Attdef" && e.tag === attrib.tag,
        );
        const matchingAttdef = found?.type === "Attdef" ? found : undefined;
        // #470 (Lane F) — ONE-TRUTH world-vs-block-local placement: a SET
        // `overridePosition` is a WORLD point that rides only the pure grid
        // residual (never the INSERT affine again — the pre-#470 double-transform
        // bug); an UNSET one follows the ATTDEF template through the full affine.
        const placement = resolveExplodedAttribPlacement({
          attrib,
          matchingAttdef,
          matrix,
          // #482 — the WORLD residual rides the block's rotate+scale (render
          // truth `transformBlockDirection`), NOT the raw `origin − baseOrigin`
          // step; ONE-TRUTH with the persisted reducer via the shared helper.
          cellDelta: resolveMinsertCellOffset({
            row,
            col,
            columnSpacing: insert.columnSpacing,
            rowSpacing: insert.rowSpacing,
            rotationDeg: parentRot,
            scaleX,
            scaleY,
          }),
          parentRotationDeg: parentRot,
          scaleX,
        });
        const textGeom: SimpleTextEntity = {
          id: input.allocateId(),
          type: "Text",
          spaceId: insert.spaceId,
          insertionPoint: placement.insertionPoint,
          height: placement.height,
          rotation: placement.rotation,
          contents: attrib.value,
          styleName: placement.styleName,
          widthFactor: 1,
          obliqueAngle: 0,
          hJustification: 0,
          vJustification: 0,
          thickness: 0,
          // GitHub Codex review PR #558 round 2 P2 — shell↔doc parity with
          // `blockReducerHelpers.ts`'s persisted-document reducer (see its
          // own doc for the full rationale).
          generationFlags: placement.generationFlags,
        };
        newEntities.push(
          buildTextPreviewEntity(textGeom, {
            summary: `Exploded ATTRIB ${attrib.tag} = ${attrib.value}`,
          }),
        );
      }

      for (const child of blockDefinition.entities) {
        if (child.type === "Attdef") continue; // ATTDEFs don't copy out
        const exploded = explodeChildEntity({
          child,
          matrix,
          parentInsert: insert,
          parentRotationDeg: parentRot,
          scaleX,
          scaleY,
          scaleZ,
          basePointZ: blockDefinition.basePoint.z,
          allocateId: input.allocateId,
          resolveNested,
          blocks: input.blocks,
        });
        newEntities.push(...exploded);
      }
    }
  }

  return {
    removedEntityIds: [insert.id],
    newEntities,
    declinedChildTypes: [],
    summary: `EXPLODE ${insert.blockName}: produced ${newEntities.length} entit${newEntities.length === 1 ? "y" : "ies"}`,
  };
}

type ExplodeChildInput = {
  child: BlockChildEntity;
  matrix: BlockMat;
  parentInsert: DrawingBlockInsertEntity;
  parentRotationDeg: number;
  scaleX: number;
  scaleY: number;
  // SL-15 — the INSERT z-scale + the block definition base-point z. The 2D
  // `matrix` carries only XY; the ellipse arm needs both to lift a tilted /
  // elevated ellipse child into world z (F3/F4/F6/F7/F8) exactly as the
  // persisted reducer does, so the shell preview does not flash a flattened
  // ellipse before reconcile.
  scaleZ: number;
  basePointZ: number;
  allocateId: () => string;
  resolveNested: ResolveNestedMode;
  blocks: Record<string, BlockDefinition>;
};

/**
 * Explode a single block child. Handles Circle/Arc → Ellipse
 * promotion when the INSERT has non-uniform scaling.
 */
function explodeChildEntity(input: ExplodeChildInput): EntityPreview[] {
  const {
    child,
    matrix,
    parentInsert,
    parentRotationDeg,
    scaleX,
    scaleY,
    scaleZ,
    basePointZ,
    allocateId,
  } = input;
  const id = allocateId();
  const base = cloneChildBase(child, id, parentInsert);
  const uniform = Math.abs(Math.abs(scaleX) - Math.abs(scaleY)) < 1e-9;

  switch (child.type) {
    case "Line": {
      const start = transformPoint(matrix, child.start);
      const end = transformPoint(matrix, child.end);
      const lineGeom: SimpleLineEntity = {
        id,
        type: "Line",
        spaceId: parentInsert.spaceId,
        start: { x: start.x, y: start.y },
        end: { x: end.x, y: end.y },
      };
      return [
        buildSimpleGeometryPreviewEntity(lineGeom, {
          summary: `Exploded Line from ${parentInsert.blockName}`,
          properties: {
            Layer: base.layer,
            Color: base.color,
            Linetype: base.linetype,
            Lineweight: base.lineweight,
            Thickness: String(child.thickness ?? 0),
          },
        }),
      ];
    }

    case "Circle": {
      const newCenter = transformPoint(matrix, child.center);
      if (uniform) {
        const newRadius = child.radius * Math.abs(scaleX);
        const circleGeom: SimpleCircleEntity = {
          id,
          type: "Circle",
          spaceId: parentInsert.spaceId,
          center: { x: newCenter.x, y: newCenter.y },
          radius: newRadius,
        };
        return [
          buildSimpleGeometryPreviewEntity(circleGeom, {
            summary: `Exploded Circle from ${parentInsert.blockName}`,
            properties: {
              Layer: base.layer,
              Color: base.color,
              Linetype: base.linetype,
              Lineweight: base.lineweight,
              Thickness: String(child.thickness ?? 0),
            },
          }),
        ];
      }
      // Non-uniform: promote to Ellipse. Major axis is (scaled + rotated)
      // (r, 0) in the block's local frame — transformed by the pure
      // vector transform (no translation) so we get the world-space
      // axis vector. Ratio = |scaleY|/|scaleX|. The full ellipse has
      // start/end parameters 0 and 2π.
      const majorLocal = { x: child.radius, y: 0 };
      const majorWorld = transformVector(matrix, majorLocal);
      const ellipseGeom: SimpleEllipseEntity = {
        id,
        type: "Ellipse",
        spaceId: parentInsert.spaceId,
        center: { x: newCenter.x, y: newCenter.y },
        majorAxisEndpoint: majorWorld,
        ratio: Math.abs(scaleY) / Math.abs(scaleX),
        startParam: 0,
        endParam: 2 * Math.PI,
      };
      return [
        buildEllipsePreviewEntity(ellipseGeom, {
          summary: `Exploded Circle → Ellipse from ${parentInsert.blockName} (non-uniform scale)`,
          properties: {
            Layer: base.layer,
            Color: base.color,
            Linetype: base.linetype,
            Lineweight: base.lineweight,
            Thickness: String(child.thickness ?? 0),
          },
        }),
      ];
    }

    case "Arc": {
      const newCenter = transformPoint(matrix, child.center);
      if (uniform) {
        const newRadius = child.radius * Math.abs(scaleX);
        const arcGeom: SimpleArcEntity = {
          id,
          type: "Arc",
          spaceId: parentInsert.spaceId,
          center: { x: newCenter.x, y: newCenter.y },
          radius: newRadius,
          startAngle: child.startAngle + parentRotationDeg,
          endAngle: child.endAngle + parentRotationDeg,
        };
        return [
          buildSimpleGeometryPreviewEntity(arcGeom, {
            summary: `Exploded Arc from ${parentInsert.blockName}`,
            properties: {
              Layer: base.layer,
              Color: base.color,
              Linetype: base.linetype,
              Lineweight: base.lineweight,
              Thickness: String(child.thickness ?? 0),
            },
          }),
        ];
      }
      // Non-uniform arc → elliptical arc.
      const majorLocal = { x: child.radius, y: 0 };
      const majorWorld = transformVector(matrix, majorLocal);
      const startRad = (child.startAngle * Math.PI) / 180;
      const endRad = (child.endAngle * Math.PI) / 180;
      const ellipseGeom: SimpleEllipseEntity = {
        id,
        type: "Ellipse",
        spaceId: parentInsert.spaceId,
        center: { x: newCenter.x, y: newCenter.y },
        majorAxisEndpoint: majorWorld,
        ratio: Math.abs(scaleY) / Math.abs(scaleX),
        startParam: startRad,
        endParam: endRad,
      };
      return [
        buildEllipsePreviewEntity(ellipseGeom, {
          summary: `Exploded Arc → Elliptical arc from ${parentInsert.blockName} (non-uniform scale)`,
          properties: {
            Layer: base.layer,
            Color: base.color,
            Linetype: base.linetype,
            Lineweight: base.lineweight,
            Thickness: String(child.thickness ?? 0),
          },
        }),
      ];
    }

    case "Ellipse": {
      // SL-15 — project the SAME 3D geometry the persisted reducer produces
      // (F3 major-axis z, F4/F7 center world z, F5 3D-conjugate ratio, F6/F8
      // extrusion normal) via the shared ONE-TRUTH helper, so the transient
      // shell preview does NOT flash a flattened ellipse before the reducer
      // reconciles (shell↔doc parity). `child.extrusion` is schema-defaulted to
      // (0,0,1) on a typed ellipse.
      const geom = computeExplodedEllipseGeometry(
        {
          center: child.center,
          majorAxisEndpoint: child.majorAxisEndpoint,
          ratio: child.ratio,
          extrusion: child.extrusion,
          // Params re-anchor to the recovered principal frame under a rotated /
          // non-uniform INSERT (SL-15 round-8 @codex F2); a full ellipse /
          // frame-preserving case returns them byte-identical (shell↔doc parity
          // with the persisted reducer, which passes the SAME params).
          startParam: child.startParam,
          endParam: child.endParam,
        },
        {
          matrix,
          scaleZ,
          insertionZ: parentInsert.insertionPoint.z ?? 0,
          basePointZ,
        },
      );
      const ellipseGeom: SimpleEllipseEntity = {
        id,
        type: "Ellipse",
        spaceId: parentInsert.spaceId,
        center: geom.center,
        majorAxisEndpoint: geom.majorAxisEndpoint,
        ratio: geom.ratio,
        startParam: geom.startParam ?? child.startParam,
        endParam: geom.endParam ?? child.endParam,
        // Emit the OCS normal ONLY when non-default — a planar child under an
        // orientation-preserving INSERT stays byte-identical (no 210/220/230).
        ...(geom.extrusionIsDefault ? {} : { extrusion: geom.extrusion }),
      };
      return [
        buildEllipsePreviewEntity(ellipseGeom, {
          summary: `Exploded Ellipse from ${parentInsert.blockName}`,
          properties: {
            Layer: base.layer,
            Color: base.color,
            Linetype: base.linetype,
            Lineweight: base.lineweight,
            Thickness: String(child.thickness ?? 0),
          },
        }),
      ];
    }

    case "LWPOLYLINE": {
      const lwGeom: SimpleLwpolylineEntity = {
        id,
        type: "LWPOLYLINE",
        spaceId: parentInsert.spaceId,
        vertices: child.vertices.map((v) => {
          const t = transformPoint(matrix, { x: v.x, y: v.y });
          return {
            x: t.x,
            y: t.y,
            bulge: v.bulge,
            startWidth: v.startWidth,
            endWidth: v.endWidth,
          };
        }),
        closed: child.closed,
        // DXF group 70 bit 128 — plinegen survives EXPLODE (AutoCAD parity:
        // the exploded polyline keeps its linetype-generation flag; the block
        // child is the full `lwpolylineEntitySchema`, so the typed flag is
        // available here). Only-set-when-true mirrors the bridge convention.
        ...(child.plinegen ? { plinegen: true } : {}),
        constantWidth: child.constantWidth,
        elevation: child.elevation,
        thickness: child.thickness,
      };
      return [
        buildLwpolylinePreviewEntity(lwGeom, {
          summary: `Exploded LWPOLYLINE from ${parentInsert.blockName}`,
          properties: {
            Layer: base.layer,
            Color: base.color,
            Linetype: base.linetype,
            Lineweight: base.lineweight,
            Thickness: String(child.thickness),
          },
        }),
      ];
    }

    case "Text": {
      const t = transformPoint(matrix, child.insertionPoint);
      const textGeom: SimpleTextEntity = {
        id,
        type: "Text",
        spaceId: parentInsert.spaceId,
        insertionPoint: t,
        height: child.height * Math.abs(scaleX),
        rotation: child.rotation + parentRotationDeg,
        contents: child.contents,
        styleName: child.styleName,
        widthFactor: child.widthFactor,
        obliqueAngle: child.obliqueAngle,
        hJustification: child.hJustification,
        vJustification: child.vJustification,
        thickness: child.thickness,
      };
      return [
        buildTextPreviewEntity(textGeom, {
          summary: `Exploded Text from ${parentInsert.blockName}`,
          properties: {
            Layer: base.layer,
            Color: base.color,
            Linetype: base.linetype,
            Lineweight: base.lineweight,
            Thickness: String(child.thickness),
          },
        }),
      ];
    }

    case "MText": {
      const t = transformPoint(matrix, child.insertionPoint);
      const mtextGeom: SimpleMtextEntity = {
        id,
        type: "MText",
        spaceId: parentInsert.spaceId,
        insertionPoint: t,
        nominalHeight: child.nominalHeight * Math.abs(scaleX),
        referenceWidth: child.referenceWidth * Math.abs(scaleX),
        attachmentPoint: child.attachmentPoint,
        drawingDirection: child.drawingDirection,
        contents: child.contents,
        styleName: child.styleName,
        rotation: child.rotation + parentRotationDeg,
        lineSpacingStyle: child.lineSpacingStyle,
        lineSpacingFactor: child.lineSpacingFactor,
        backgroundFill: child.backgroundFill,
      };
      return [
        buildMtextPreviewEntity(mtextGeom, {
          summary: `Exploded MText from ${parentInsert.blockName}`,
          properties: {
            Layer: base.layer,
            Color: base.color,
            Linetype: base.linetype,
            Lineweight: base.lineweight,
            Thickness: "0",
          },
        }),
      ];
    }

    case "Block": {
      // Nested INSERT. With resolveNested="one-level" or "none" we
      // preserve it as an INSERT — AutoCAD's EXPLODE is single-level;
      // a user can re-explode to go deeper (module `nestedInsertExplode.ts`
      // "AutoCAD parity"). `resolveNested` selects only single-level here;
      // deeper recursion is a future mode (kept in the input contract).
      const _ignored: ResolveNestedMode = input.resolveNested;
      void _ignored;
      // ONE-TRUTH placement shared with the persisted reducer (shell↔doc
      // parity): the 3-axis scale COMPOSES (scaleZ was a child-drop before),
      // rotation composes, spacing stays verbatim (the render re-applies the
      // composed scale to the MINSERT step). The transient shell preview
      // carries only these placement fields; the reconciled document (reducer)
      // additionally preserves the child's extrusion / dynamic / annotative
      // data + world-lifts its ATTRIBs (fields `SimpleBlockInsertEntity` /
      // `EntityPreview` cannot represent).
      const placement = composeNestedInsertPlacement({
        child,
        matrix,
        parentRotationDeg,
        scaleX,
        scaleY,
        scaleZ,
      });
      const nestedGeom: SimpleBlockInsertEntity = {
        id,
        type: "Block",
        spaceId: parentInsert.spaceId,
        blockName: child.blockName,
        insertionPoint: placement.insertionPoint,
        scaleX: placement.scaleX,
        scaleY: placement.scaleY,
        scaleZ: placement.scaleZ,
        rotation: placement.rotation,
        columnCount: placement.columnCount,
        rowCount: placement.rowCount,
        columnSpacing: placement.columnSpacing,
        rowSpacing: placement.rowSpacing,
      };
      return [
        buildBlockInsertPreviewEntity(nestedGeom, {
          summary: `Exploded nested INSERT ${child.blockName} (kept as INSERT)`,
          properties: {
            Layer: base.layer,
            Color: base.color,
            Linetype: base.linetype,
            Lineweight: base.lineweight,
          },
        }),
      ];
    }

    default:
      return [];
  }
}

// ═══════════════════════════════════════════════════════════════
// ANNOUPDATE / OBJECTSCALE (Phase FIN.2 — Annotative blocks)
// ═══════════════════════════════════════════════════════════════

interface AnnotativeInsertUpdate {
  insertId: string;
  spaceId: string;
  oldScaleX: number;
  oldScaleY: number;
  newScaleX: number;
  newScaleY: number;
}

export interface AnnoupdateInput {
  /** Block records — used to identify which blocks are annotative. */
  blockRecords: Record<string, BlockRecord>;
  /** Document-level cannoscale factor (50 for 1:50, 100 for 1:100, …). */
  cannoscale: number;
  /** All INSERT entities in the document, with their ids and current
   *  scaleX/scaleY/blockName. We model the input weakly so the caller
   *  can pass either the live drawing entities or the shell preview
   *  entities — the mutation just rewrites the scale fields. */
  inserts: Array<{
    id: string;
    spaceId: string;
    blockName: string;
    scaleX: number;
    scaleY: number;
  }>;
}

export interface AnnoupdateResult {
  /** Per-INSERT scale updates that should be applied. */
  updates: AnnotativeInsertUpdate[];
  /** Summary suitable for the audit log / status bar. */
  summary: string;
}

/**
 * Apply the current cannoscale to every annotative INSERT. The mutation
 * does not directly mutate the document — it returns an array of
 * per-INSERT updates that the reducer iterates over. The scale factor
 * is the cannoscale's reciprocal (1:50 → 0.02 multiplier in paper
 * units), but for the model-space behaviour of annotative blocks the
 * native rule is "scale the block by 1/cannoscale_factor" so the block
 * occupies 1× a paper unit at any drawing scale. We adopt that rule.
 */
export function annoupdateMutation(input: AnnoupdateInput): AnnoupdateResult {
  const updates: AnnotativeInsertUpdate[] = [];
  if (input.cannoscale === 0) {
    return { updates, summary: "ANNOUPDATE skipped: cannoscale is zero" };
  }
  const factor = 1 / input.cannoscale;
  for (const insert of input.inserts) {
    const record = input.blockRecords[insert.blockName];
    if (!record || !record.isAnnotative) continue;
    if (
      Math.abs(insert.scaleX - factor) < 1e-9 &&
      Math.abs(insert.scaleY - factor) < 1e-9
    ) {
      continue; // already at the right scale
    }
    updates.push({
      insertId: insert.id,
      spaceId: insert.spaceId,
      oldScaleX: insert.scaleX,
      oldScaleY: insert.scaleY,
      newScaleX: factor,
      newScaleY: factor,
    });
  }
  return {
    updates,
    summary:
      updates.length === 0
        ? "ANNOUPDATE: no annotative INSERT needed updating"
        : `ANNOUPDATE: ${updates.length} annotative INSERT${
            updates.length === 1 ? "" : "s"
          } scaled to ${input.cannoscale}`,
  };
}

export interface ObjectScaleAddInput {
  insertId: string;
  scaleName: string;
  /** Current annotation scales for the INSERT. */
  currentScales: string[];
}

export interface ObjectScaleRemoveInput {
  insertId: string;
  scaleName: string;
  currentScales: string[];
}

export interface ObjectScaleResult {
  insertId: string;
  newScales: string[];
  summary: string;
}

/**
 * Add an annotation scale name to the INSERT's annotationScales list.
 * Idempotent: returns the original list when the scale is already
 * present.
 */
export function objectScaleAddMutation(
  input: ObjectScaleAddInput,
): ObjectScaleResult {
  if (input.currentScales.includes(input.scaleName)) {
    return {
      insertId: input.insertId,
      newScales: input.currentScales,
      summary: `OBJECTSCALE: ${input.scaleName} already present`,
    };
  }
  return {
    insertId: input.insertId,
    newScales: [...input.currentScales, input.scaleName],
    summary: `OBJECTSCALE: added ${input.scaleName}`,
  };
}

/**
 * Remove an annotation scale name from the INSERT's annotationScales
 * list. Idempotent: returns the original list when the scale is absent.
 */
export function objectScaleRemoveMutation(
  input: ObjectScaleRemoveInput,
): ObjectScaleResult {
  if (!input.currentScales.includes(input.scaleName)) {
    return {
      insertId: input.insertId,
      newScales: input.currentScales,
      summary: `OBJECTSCALE: ${input.scaleName} was not present`,
    };
  }
  return {
    insertId: input.insertId,
    newScales: input.currentScales.filter((name) => name !== input.scaleName),
    summary: `OBJECTSCALE: removed ${input.scaleName}`,
  };
}
